import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { getOfferingVertical } from './offeringCustomizationVertical';

const Page = styled.main`max-width: 780px; margin: 0 auto; padding: 2rem 1rem 5rem;`;
const Title = styled.h1`color: var(--text-primary); font-size: 2rem; margin: 0 0 1.25rem;`;
const Card = styled.section`
  background: var(--bg-primary, #fff); border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 0.75rem; margin-bottom: 1rem; padding: 1.2rem;
`;
const ToggleRow = styled.label`align-items: flex-start; cursor: pointer; display: flex; gap: 1rem; justify-content: space-between;`;
const SectionTitle = styled.h2`color: var(--text-primary); font-size: 1rem; margin: 0 0 0.45rem;`;
const Helper = styled.p`color: var(--text-secondary); font-size: 0.82rem; line-height: 1.5; margin: 0.35rem 0 0;`;
const Radio = styled.label`align-items: center; cursor: pointer; display: flex; gap: 0.55rem; padding: 0.5rem 0;`;
const Label = styled.label`display: block; font-size: 0.875rem; font-weight: 700; margin: 1rem 0 0.4rem;`;
const Input = styled.input`
  border: 1.5px solid ${(p) => (p.$error ? '#dc2626' : 'var(--border-color, #d1d5db)')};
  border-radius: 0.55rem; box-sizing: border-box; font: inherit; padding: 0.75rem; width: 100%;
`;
const Select = styled.select`
  border: 1.5px solid var(--border-color, #d1d5db);
  border-radius: 0.55rem; box-sizing: border-box; font: inherit; padding: 0.75rem; width: 100%;
`;
const Textarea = styled.textarea`
  border: 1.5px solid var(--border-color, #d1d5db); border-radius: 0.55rem;
  box-sizing: border-box; font: inherit; min-height: 90px; padding: 0.75rem; width: 100%;
`;
const Error = styled.p`color: #b91c1c; font-size: 0.8rem; margin: 0.35rem 0 0;`;
const Save = styled.button`
  background: #0f3d36; border: 0; border-radius: 0.55rem; color: #fff; cursor: pointer;
  font-weight: 800; padding: 0.85rem 1.3rem; &:disabled { cursor: not-allowed; opacity: 0.55; }
`;
const Ghost = styled.button`
  background: #fff; border: 1.5px solid #0f3d36; border-radius: 0.55rem; color: #0f3d36;
  cursor: pointer; font-weight: 800; padding: 0.7rem 1rem;
`;
const Danger = styled.button`
  background: #fff; border: 1.5px solid #b91c1c; border-radius: 0.55rem; color: #b91c1c;
  cursor: pointer; font-weight: 800; padding: 0.55rem 0.85rem;
`;
const Row = styled.div`display: flex; flex-wrap: wrap; gap: 0.65rem; margin-top: 0.75rem;`;
const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.45rem;
`;
const OptionInput = styled(Input)`
  flex: 1;
  min-width: 0;
  width: auto;
`;
const FieldCard = styled.div`border: 1px solid #e2e8f0; border-radius: 0.65rem; margin-top: 0.75rem; padding: 0.85rem;`;
const State = styled.div`padding: 4rem 1rem; text-align: center;`;
const Back = styled(Link)`color: #0f3d36; display: inline-block; font-weight: 700; margin-bottom: 1rem;`;
const Visibility = styled.div`
  background: ${(p) => (p.$ok ? '#ecfdf5' : '#fff7ed')};
  border: 1px solid ${(p) => (p.$ok ? '#a7f3d0' : '#fdba74')};
  border-radius: 0.65rem; color: ${(p) => (p.$ok ? '#065f46' : '#9a3412')};
  font-size: 0.9rem; line-height: 1.5; margin-bottom: 1rem; padding: 0.95rem 1rem;
`;

const ANSWER_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'yes_no', label: 'Yes / No' },
  { value: 'single_choice', label: 'Multiple choices' },
];

const emptyOffering = () => ({
  id: null,
  title: '',
  description: '',
  tags: '',
  min_price: '',
  is_active: true,
  fields: [],
});

const emptyField = () => ({
  key: '',
  label: '',
  type: 'text',
  required: false,
  options: [''],
  help_text: '',
});

function ownerAnswerType(type) {
  if (type === 'yes_no') return 'yes_no';
  if (type === 'single_choice' || type === 'multi_choice') return 'single_choice';
  return 'text';
}

/** Stable machine id for answers; shop owners only need to write the question text. */
function fieldKeyFromLabel(label, used) {
  let base = String(label || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  if (!base) base = 'field';
  let key = base;
  let n = 2;
  while (used.has(key)) {
    const suffix = `_${n}`;
    key = `${base.slice(0, Math.max(1, 40 - suffix.length))}${suffix}`;
    n += 1;
  }
  used.add(key);
  return key;
}

export function OfferingCustomizationSettingsPage({ verticalKey = 'boutique' }) {
  const vertical = getOfferingVertical(verticalKey);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accepts, setAccepts] = useState(false);
  const [priceMode, setPriceMode] = useState('discussed');
  const [minimum, setMinimum] = useState('');
  const [errors, setErrors] = useState({});
  const [offerings, setOfferings] = useState([]);
  const [editor, setEditor] = useState(null);
  const [savingOffering, setSavingOffering] = useState(false);
  const [listedForBuyers, setListedForBuyers] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState('');
  const [activeOfferingsCount, setActiveOfferingsCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [settings, offeringData] = await Promise.all([
        vertical.getSettings(),
        vertical.listOfferings(),
      ]);
      setAccepts(Boolean(settings.accepts_customizations));
      setPriceMode(settings.pricing_to_be_discussed ? 'discussed' : 'minimum');
      setMinimum(
        settings.customization_min_price_cents == null
          ? ''
          : String(Number(settings.customization_min_price_cents) / 100),
      );
      setOfferings(offeringData.results || []);
      setListedForBuyers(Boolean(settings.listed_for_buyers));
      setApprovalStatus(settings.approval_status || '');
      setActiveOfferingsCount(Number(settings.active_offerings_count || 0));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [vertical]);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    const nextErrors = {};
    const parsedMinimum = Number(minimum);
    if (accepts && priceMode === 'minimum' && (!Number.isFinite(parsedMinimum) || parsedMinimum <= 0)) {
      nextErrors.minimum = 'Enter a minimum customization price greater than 0.';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setSaving(true);
    try {
      const settings = await vertical.updateSettings({
        accepts_customizations: accepts,
        customization_min_price_cents:
          accepts && priceMode === 'minimum' ? Math.round(parsedMinimum * 100) : null,
        pricing_to_be_discussed: accepts && priceMode === 'discussed',
      });
      const nextActiveCount = Number(settings.active_offerings_count || 0);
      const nextApproval = settings.approval_status || '';
      setListedForBuyers(Boolean(settings.listed_for_buyers));
      setApprovalStatus(nextApproval);
      setActiveOfferingsCount(nextActiveCount);
      toast.success('Settings saved.');
      if (accepts && !settings.listed_for_buyers) {
        toast.info(
          nextActiveCount < 1
            ? 'Add an active offering below so buyers can find your shop.'
            : nextApproval !== 'approved'
              ? 'Your shop must be approved before it appears to buyers.'
              : 'Your shop is not visible to buyers yet.',
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Something went wrong. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const openEditor = (offering = null) => {
    if (!offering) {
      setEditor(emptyOffering());
      return;
    }
    setEditor({
      id: offering.id,
      title: offering.title || '',
      description: offering.description || '',
      tags: (offering.tags || []).join(', '),
      min_price: offering.min_price_cents == null ? '' : String(Number(offering.min_price_cents) / 100),
      is_active: Boolean(offering.is_active),
      fields: (offering.fields_schema || []).map((field) => ({
        key: field.key || '',
        label: field.label || '',
        type: ownerAnswerType(field.type),
        required: Boolean(field.required),
        options: (field.options || []).length ? field.options : [''],
        help_text: field.help_text || '',
      })),
    });
  };

  const saveOffering = async () => {
    if (!editor?.title?.trim()) {
      toast.error('Offering title is required.');
      return;
    }
    const missingChoices = editor.fields.some((field) => {
      const answerType = ownerAnswerType(field.type);
      if (answerType !== 'single_choice') return false;
      const options = Array.isArray(field.options)
        ? field.options
        : String(field.options || '').split(',');
      return !options.map((item) => String(item).trim()).filter(Boolean).length;
    });
    if (missingChoices) {
      toast.error('Add at least one choice for each multiple-choice question.');
      return;
    }
    const payload = {
      title: editor.title.trim(),
      description: editor.description.trim(),
      tags: editor.tags
        .split(',')
        .map((tag) => tag.trim().toLowerCase())
        .filter(Boolean),
      min_price_cents: editor.min_price ? Math.round(Number(editor.min_price) * 100) : null,
      is_active: editor.is_active,
      fields_schema: (() => {
        const usedKeys = new Set();
        return editor.fields.map((field) => {
          const key = field.key?.trim()
            ? field.key.trim().toLowerCase().replace(/\s/g, '_')
            : fieldKeyFromLabel(field.label, usedKeys);
          if (field.key?.trim()) usedKeys.add(key);
          const answerType = ownerAnswerType(field.type);
          return {
            key,
            label: field.label,
            type: answerType,
            required: field.required,
            help_text: field.help_text || undefined,
            options: answerType === 'single_choice'
              ? (Array.isArray(field.options) ? field.options : String(field.options || '').split(','))
                  .map((item) => String(item).trim())
                  .filter(Boolean)
              : [],
          };
        });
      })(),
    };
    setSavingOffering(true);
    try {
      if (editor.id) {
        await vertical.updateOffering(editor.id, payload);
      } else {
        await vertical.createOffering(payload);
      }
      toast.success('Offering saved.');
      setEditor(null);
      await load();
    } catch (err) {
      const data = err.response?.data;
      toast.error(
        data?.fields_schema?.[0]
        || data?.error
        || data?.detail
        || 'Could not save offering.',
      );
    } finally {
      setSavingOffering(false);
    }
  };

  if (loading) return <LoadingSpinner text="Loading settings…" />;
  if (loadError) {
    return (
      <Page>
        <State>
          <h2>We couldn’t load {vertical.label} settings.</h2>
          <Save type="button" onClick={load}>Try again</Save>
        </State>
      </Page>
    );
  }

  return (
    <Page>
      <Back to={vertical.ownerInboxPath}>← Custom order requests</Back>
      <Title>{vertical.titleCase} customizations</Title>

      {accepts ? (
        <Visibility $ok={listedForBuyers}>
          {listedForBuyers
            ? 'Buyers can find your shop when they browse custom designers.'
            : [
                'Your shop is not visible to buyers yet.',
                approvalStatus !== 'approved' ? 'Shop approval is still pending.' : null,
                activeOfferingsCount < 1 ? 'Add at least one active offering below.' : null,
              ].filter(Boolean).join(' ')}
        </Visibility>
      ) : null}

      <Card>
        <ToggleRow>
          <div>
            <SectionTitle>Accept custom orders</SectionTitle>
            <Helper>Buyers can only request custom work when this is on and you have active offerings.</Helper>
          </div>
          <input type="checkbox" checked={accepts} onChange={(event) => setAccepts(event.target.checked)} />
        </ToggleRow>
        {accepts ? (
          <>
            <Radio>
              <input type="radio" checked={priceMode === 'discussed'} onChange={() => setPriceMode('discussed')} />
              Pricing to be discussed
            </Radio>
            <Radio>
              <input type="radio" checked={priceMode === 'minimum'} onChange={() => setPriceMode('minimum')} />
              Show a shop-level minimum price
            </Radio>
            {priceMode === 'minimum' ? (
              <>
                <Label>Minimum price (EGP)</Label>
                <Input $error={Boolean(errors.minimum)} value={minimum} onChange={(event) => setMinimum(event.target.value)} />
                {errors.minimum ? <Error>{errors.minimum}</Error> : null}
              </>
            ) : null}
          </>
        ) : null}
        <Row>
          <Save type="button" disabled={saving} onClick={saveSettings}>
            {saving ? 'Saving…' : 'Save settings'}
          </Save>
        </Row>
      </Card>

      <Card>
        <SectionTitle>Offerings</SectionTitle>
        <Helper>Each offering is a custom service buyers can choose, with its own form fields.</Helper>
        <Row>
          <Ghost type="button" onClick={() => openEditor()}>Add offering</Ghost>
        </Row>
        {offerings.map((offering) => (
          <FieldCard key={offering.id}>
            <strong>{offering.title}</strong>
            <Helper>
              {offering.is_active ? 'Active' : 'Inactive'}
              {(offering.tags || []).length ? ` · ${(offering.tags || []).join(', ')}` : ''}
              {` · ${(offering.fields_schema || []).length} field(s)`}
            </Helper>
            <Row>
              <Ghost type="button" onClick={() => openEditor(offering)}>Edit</Ghost>
              {offering.is_active ? (
                <Danger
                  type="button"
                  onClick={async () => {
                    await vertical.deleteOffering(offering.id);
                    toast.success('Offering deactivated.');
                    load();
                  }}
                >
                  Deactivate
                </Danger>
              ) : null}
            </Row>
          </FieldCard>
        ))}
      </Card>

      {editor ? (
        <Card>
          <SectionTitle>{editor.id ? 'Edit offering' : 'New offering'}</SectionTitle>
          <Label>Title</Label>
          <Input value={editor.title} onChange={(event) => setEditor({ ...editor, title: event.target.value })} />
          <Label>Description</Label>
          <Textarea value={editor.description} onChange={(event) => setEditor({ ...editor, description: event.target.value })} />
          <Label>Tags (comma separated)</Label>
          <Input value={editor.tags} onChange={(event) => setEditor({ ...editor, tags: event.target.value })} />
          <Label>Minimum price hint (EGP, optional)</Label>
          <Input value={editor.min_price} onChange={(event) => setEditor({ ...editor, min_price: event.target.value })} />
          <Radio>
            <input
              type="checkbox"
              checked={editor.is_active}
              onChange={(event) => setEditor({ ...editor, is_active: event.target.checked })}
            />
            Active
          </Radio>

          <SectionTitle style={{ marginTop: '1.25rem' }}>
            Questions to ask buyers from my shop to be able to customize their orders
          </SectionTitle>
          {editor.fields.map((field, index) => (
            <FieldCard key={`field-${index}`}>
              <Label>Question</Label>
              <Input
                value={field.label}
                placeholder="e.g. What fabric do you prefer?"
                onChange={(event) => {
                  const fields = [...editor.fields];
                  fields[index] = { ...field, label: event.target.value };
                  setEditor({ ...editor, fields });
                }}
              />
              <Label>Answer type</Label>
              <Select
                value={ownerAnswerType(field.type)}
                onChange={(event) => {
                  const nextType = event.target.value;
                  const fields = [...editor.fields];
                  fields[index] = {
                    ...field,
                    type: nextType,
                    options: nextType === 'single_choice'
                      ? (field.options?.length ? field.options : [''])
                      : [],
                  };
                  setEditor({ ...editor, fields });
                }}
              >
                {ANSWER_TYPES.map((answerType) => (
                  <option key={answerType.value} value={answerType.value}>
                    {answerType.label}
                  </option>
                ))}
              </Select>
              {ownerAnswerType(field.type) === 'text' ? (
                <Helper>Buyers will answer in a text box.</Helper>
              ) : null}
              {ownerAnswerType(field.type) === 'yes_no' ? (
                <Helper>Buyers will choose Yes or No.</Helper>
              ) : null}
              {ownerAnswerType(field.type) === 'single_choice' ? (
                <>
                  <Label>Choices</Label>
                  {(field.options?.length ? field.options : ['']).map((option, optionIndex) => (
                    <OptionRow key={`option-${index}-${optionIndex}`}>
                      <OptionInput
                        value={option}
                        placeholder={`Choice ${optionIndex + 1}`}
                        onChange={(event) => {
                          const fields = [...editor.fields];
                          const options = [...(field.options?.length ? field.options : [''])];
                          options[optionIndex] = event.target.value;
                          fields[index] = { ...field, options };
                          setEditor({ ...editor, fields });
                        }}
                      />
                      <Danger
                        type="button"
                        onClick={() => {
                          const fields = [...editor.fields];
                          const options = (field.options || []).filter((_, i) => i !== optionIndex);
                          fields[index] = { ...field, options: options.length ? options : [''] };
                          setEditor({ ...editor, fields });
                        }}
                      >
                        Remove
                      </Danger>
                    </OptionRow>
                  ))}
                  <Ghost
                    type="button"
                    onClick={() => {
                      const fields = [...editor.fields];
                      fields[index] = { ...field, options: [...(field.options || []), ''] };
                      setEditor({ ...editor, fields });
                    }}
                  >
                    Add choice
                  </Ghost>
                </>
              ) : null}
              <Label>Help text</Label>
              <Input
                value={field.help_text}
                onChange={(event) => {
                  const fields = [...editor.fields];
                  fields[index] = { ...field, help_text: event.target.value };
                  setEditor({ ...editor, fields });
                }}
              />
              <Radio>
                <input
                  type="checkbox"
                  checked={field.required}
                  onChange={(event) => {
                    const fields = [...editor.fields];
                    fields[index] = { ...field, required: event.target.checked };
                    setEditor({ ...editor, fields });
                  }}
                />
                Required
              </Radio>
              <Danger
                type="button"
                onClick={() => {
                  setEditor({
                    ...editor,
                    fields: editor.fields.filter((_, fieldIndex) => fieldIndex !== index),
                  });
                }}
              >
                Remove field
              </Danger>
            </FieldCard>
          ))}
          <Row>
            <Ghost type="button" onClick={() => setEditor({ ...editor, fields: [...editor.fields, emptyField()] })}>
              Add field
            </Ghost>
            <Save type="button" disabled={savingOffering} onClick={saveOffering}>
              {savingOffering ? 'Saving…' : 'Save offering'}
            </Save>
            <Ghost type="button" onClick={() => setEditor(null)}>Cancel</Ghost>
          </Row>
        </Card>
      ) : null}
    </Page>
  );
}

const BoutiqueCustomizationSettingsPage = () => (
  <OfferingCustomizationSettingsPage verticalKey="boutique" />
);
export const DesignerCustomizationSettingsPage = () => (
  <OfferingCustomizationSettingsPage verticalKey="designer" />
);
export default BoutiqueCustomizationSettingsPage;
